import { Injectable, signal } from "@angular/core";
import { SceneManagerService } from "../scene-manager/scene-manager.service";
import { Action, MeshState, UndoRedoAction } from "~/src/app/shared/models/undoRedo.model";
import { Object3DUserData } from "~/src/utils/shape";
import { Subject } from "rxjs";
import { Mesh, MeshBasicMaterial, MeshStandardMaterial, Vector3 } from "three";
import { MeshVisualService } from "../designer/mesh-visual.service";
import { HelperService } from "../../shared/helpers/helper.service";
import { SetShapeUserDataService } from "../../shared/helpers/set-shape-userdata.service";

@Injectable({
    providedIn: "root",
})
export class UndoRedoService {
    private readonly maxUndoAction = 5;
    private undoStack: UndoRedoAction[] = [];
    private redoStack: UndoRedoAction[] = [];
    private isActionDone = new Subject<boolean>();
    public isActionDone$ = this.isActionDone.asObservable();
    public canUndo = signal<boolean>(false);
    public canRedo = signal<boolean>(false);
    private meshes: Mesh[] = [];

    constructor(
        private sceneManagerService: SceneManagerService,
        private meshVisualService: MeshVisualService,
        private helperService: HelperService,
        private setShapeUserDataService: SetShapeUserDataService,
    ) {
        this.sceneManagerService.meshes$.subscribe((meshes) => {
            this.meshes = meshes;
        });
    }

    public addToUndo(action: UndoRedoAction): void {
        if (action.actionType === Action.Edit && !this.sceneManagerService.hasShapes()) {
            return;
        }
        this.undoStack.push(action);
        if (this.undoStack.length > this.maxUndoAction) {
            this.undoStack.shift();
        }
        this.redoStack = [];
        this.canUndo.set(true);
        this.canRedo.set(false);
    }

    public performUndo(): void {
        this.processAction(this.undoStack, this.redoStack, true);
    }

    public performRedo(): void {
        this.processAction(this.redoStack, this.undoStack, false);
    }

    private processAction(stackFrom: UndoRedoAction[], stackTo: UndoRedoAction[], isUndo: boolean): void {
        let lastAction = stackFrom[stackFrom.length - 1];
        if (!lastAction) return;
        const lastActionMeshes = lastAction.meshes ?? [];

        switch (lastAction.actionType) {
            case Action.Add:
                if (lastAction.mesh) {
                    if (isUndo) {
                        this.sceneManagerService.removeMeshesFromScene([lastAction.mesh]);
                    } else {
                        this.sceneManagerService.addObjectsToScene([lastAction.mesh]);
                    }
                }
                break;

            case Action.CopyPaste:
            case Action.Duplicate:
                if (isUndo) {
                    this.sceneManagerService.removeMeshesFromScene(lastActionMeshes);
                } else {
                    this.sceneManagerService.addObjectsToScene(lastActionMeshes);
                }
                break;

            case Action.Delete:
                if (isUndo) {
                    // Add meshes back to scene in one batch
                    this.sceneManagerService.addObjectsToScene(lastActionMeshes);
                } else {
                    this.sceneManagerService.removeMeshesFromScene(lastActionMeshes);
                }
                break;

            case Action.Edit:
                lastAction = this.handleEditAction(lastAction);
                break;

            case Action.HandleInvalidJoints:
                this.handleInvalidShapes(lastAction, isUndo);
                break;

            case Action.DragAndDrop:
                lastAction = this.handleDragAndDrop(lastAction);
                break;
        }

        stackTo.push(lastAction);
        stackFrom.pop();

        if (this.undoStack.length) {
            this.canUndo.set(true);
        } else {
            this.canUndo.set(false);
        }

        if (this.redoStack.length) {
            this.canRedo.set(true);
        } else {
            this.canRedo.set(false);
        }
    }

    private handleDragAndDrop(action: UndoRedoAction): UndoRedoAction {
        const meshStates = action.meshStates?.map((meshState) => {
            const mesh = meshState.mesh;

            const currentState = {
                mesh,
                previousPosition: mesh.position.clone(), // save current position for redo/undo
            };

            mesh.position.copy(meshState.previousPosition!);

            mesh.userData[Object3DUserData.initialPosition] = mesh.position.clone();
            mesh.userData[Object3DUserData.cellReference] = {};
            if (Math.floor(mesh.position.y) % 10 !== 5) {
                const { row, column, cell } = this.helperService.calculateRowAndColumn(mesh);
                mesh.userData[Object3DUserData.cellReference] = { row, column, cell };
            }

            this.setShapeUserDataService.adjacentData(this.meshes, mesh);

            return currentState;
        });

        this.isActionDone.next(true);

        return {
            actionType: Action.DragAndDrop,
            meshStates,
        };
    }

    private handleEditAction(action: UndoRedoAction): UndoRedoAction {
        const newAction: UndoRedoAction = {
            actionType: Action.Edit,
            meshStates: action.meshStates?.map((meshState) => {
                const mesh = meshState.mesh;
                const currentState: MeshState = { mesh };

                //  Store & Restore Position (Fix for Hemisphere & Half-Cylinder)
                if (meshState.previousPosition) {
                    currentState.previousPosition = mesh.position.clone(); // Save current position before undo
                    mesh.position.copy(meshState.previousPosition); // Restore original position
                }
                // If the object was flipped, restore its previous orientation and rotation
                if (meshState.previousOrientation) {
                    currentState.previousOrientation = [...mesh.userData[Object3DUserData.orientationData]];
                    currentState.previousRotation = mesh.rotation.clone();
                    mesh.userData[Object3DUserData.orientationData] = [...meshState.previousOrientation];

                    if (meshState.previousRotation) {
                        mesh.rotation.copy(meshState.previousRotation);
                    }
                }

                // If the object's color was changed, restore its previous color
                if (meshState.previousOriginalColors) {
                    // Capture current colors for redo
                    const currentOriginalColors = Array.isArray(mesh.material)
                        ? (mesh.material as MeshBasicMaterial[]).map((material) =>
                              "#".concat(material.color.getHexString().toUpperCase()),
                          )
                        : [];
                    // Store for redo
                    currentState.previousOriginalColors = currentOriginalColors as string[];

                    // Apply colors based on whether we have per-face colors
                    if (meshState.previousOriginalColors && Array.isArray(mesh.material)) {
                        // Restore per-face colors
                        mesh.material.forEach((material, index) => {
                            if (
                                (material instanceof MeshBasicMaterial || material instanceof MeshStandardMaterial) &&
                                meshState.previousOriginalColors![index]
                            ) {
                                material.color.set(meshState.previousOriginalColors![index]);
                            }
                        });
                    }
                    mesh.userData[Object3DUserData.originalColors] = meshState.previousOriginalColors;
                }

                return currentState;
            }),
        };
        this.isActionDone.next(true);
        return newAction;
    }

    private handleInvalidShapes(action: UndoRedoAction, isUndo: boolean): void {
        const shapes = action.meshStates?.map((meshState) => meshState.mesh) || [];
        const validShapes =
            action.meshStates
                ?.filter((meshState) => !meshState.wasInvalid) // Only valid shapes
                .map((objState) => objState.mesh) || [];

        const invalidShapes =
            action.meshStates
                ?.filter((meshState) => meshState.wasInvalid) // Only invalid shapes
                .map((meshState) => meshState.mesh) || [];

        if (isUndo) {
            shapes.forEach((shape) => this.meshVisualService.revertToOriginalColors(shape));
        } else {
            invalidShapes.forEach((shape) => this.meshVisualService.markMeshAsInvalid(shape));
            validShapes.forEach((shape) => this.meshVisualService.markMeshAsValid(shape));
        }
        this.isActionDone.next(true);
    }

    public clearUndoRedoStack(): void {
        this.undoStack = [];
        this.redoStack = [];
        this.canUndo.set(false);
        this.canRedo.set(false);
    }
}
